import { HttpStatusCode } from "axios";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import type { AppDispatch } from "_shared/_stores/store-types";
import type Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type { NavigateFunction } from "react-router";
import type Session from "users/__session/session";
import {
  buildDraftFromProposalTemplate,
  type DraftFromProposalTemplate,
} from "work/proposal-templates/draft-from-proposal-template";
import type ProposalTemplate from "work/proposal-templates/proposal-template";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import { openUnsavedDraft } from "work/proposals/_views/drafts/open-unsaved-draft";
import { describeWhatWasLeftOut } from "work/proposals/drafts-from-previous/start-draft-from-previous-proposal";

export type StartDraftFromProposalTemplateArgs = {
  templateId: Guid;
  session: Session;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  /** The template as the server answers it now, with who on its team cannot be carried over. */
  fetchTemplate: (templateId: Guid) => Promise<ProposalTemplate>;
  /** Display names by user id, for telling the user who was left out. Best effort. */
  fetchNames: (userIds: Set<string>) => Promise<Map<string, string>>;
};

/**
 * Opens a new, unsaved draft pre-filled from a saved proposal template (#6129), the way Create from
 * Previous opens one (#1569): on Details, with nothing saved, and with a notice for each thing the
 * draft could not take.
 *
 * The template is read again here rather than taken from the picker's list, because only a fresh read
 * says who on its team has left since it was saved.
 */
export async function startDraftFromProposalTemplate(args: StartDraftFromProposalTemplateArgs): Promise<boolean> {
  const { templateId, session, dispatch, navigate, fetchTemplate, fetchNames } = args;

  let template: ProposalTemplate;
  try {
    template = await fetchTemplate(templateId);
  } catch (error) {
    console.error("Failed to read the proposal template", error);
    const status = getApiErrorStatus(error);
    enqueueSnackbar(
      status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden
        ? "This template is no longer available."
        : "Could not start a draft from this template. Please try again.",
      { variant: "error" },
    );
    return false;
  }

  let draft: DraftFromProposalTemplate;
  try {
    draft = buildDraftFromProposalTemplate({ template, session });
  } catch (error) {
    console.error("Failed to build a draft from a proposal template", error);
    enqueueSnackbar("Could not start a draft from this template. Please try again.", { variant: "error" });
    return false;
  }
  const names = await namesOf(draft, fetchNames);

  openUnsavedDraft({
    builder: draft.builder,
    session,
    dispatch,
    navigate,
    pageTab: PageTab.Details,
  });

  enqueueSnackbar(`Started a new draft from the template ${template.name}. Nothing is saved until you save it.`, {
    variant: "success",
  });
  for (const notice of describeWhatTheTemplateCouldNotGive(draft, names)) {
    enqueueSnackbar(notice, { variant: "warning", persist: true });
  }
  return true;
}

/** One sentence for each kind of thing the draft could not take from the template. */
export function describeWhatTheTemplateCouldNotGive(
  draft: Pick<DraftFromProposalTemplate, "peopleWhoLeft" | "leadersWhoCannotSign" | "unavailableDocumentCount">,
  names: Map<string, string>,
): string[] {
  const notices = describeWhatWasLeftOut({ ...draft, unavailableDocuments: [] }, names);
  const count = draft.unavailableDocumentCount;
  if (count > 0) {
    notices.push(
      `Did not copy ${count === 1 ? "1 document" : `${count} documents`} from the template, which ${
        count === 1 ? "has" : "have"
      } been deleted since it was saved.`,
    );
  }
  return notices;
}

async function namesOf(
  draft: DraftFromProposalTemplate,
  fetchNames: (userIds: Set<string>) => Promise<Map<string, string>>,
): Promise<Map<string, string>> {
  const userIds = new Set([...draft.peopleWhoLeft, ...draft.leadersWhoCannotSign].map((person) => person.userId.value));
  if (userIds.size === 0) return new Map();
  try {
    return await fetchNames(userIds);
  } catch (error) {
    console.error("Failed to name the people left out of a draft started from a template", error);
    return new Map();
  }
}
